/**
 * Module-level set of already-emitted warning keys.
 * Shared across all component instances and remounts so that a given warning
 * is only printed once per page load, regardless of how many HeatmapChart
 * (or other) instances encounter the same misconfiguration.
 *
 * Only emits in non-production environments.
 */
const emittedWarnings = new Set<string>();

/**
 * Emit a `console.warn` at most once per `key` for the lifetime of the module.
 *
 * @param key   - Stable identifier for this warning (e.g. `'HeatmapChart/quantile-diverging'`).
 *                Use a specific enough key that unrelated warnings don't collide.
 * @param message - Human-readable warning text logged to the console.
 */
export function warnOnce(key: string, message: string): void {
  if (process.env.NODE_ENV === 'production') return;
  if (emittedWarnings.has(key)) return;
  emittedWarnings.add(key);
  console.warn(message);
}

/**
 * Emit a `console.info` at most once per `key`. Use for non-error guidance
 * (e.g. "single-series sequential scale renders the mid-range stop") that
 * informs an author without signalling a misconfiguration. Shares the same
 * de-dupe set as {@link warnOnce}, so `resetAllWarnOnce` clears both.
 */
export function infoOnce(key: string, message: string): void {
  if (process.env.NODE_ENV === 'production') return;
  if (emittedWarnings.has(key)) return;
  emittedWarnings.add(key);
  console.info(message);
}

/**
 * Reset a previously emitted warning key so it can fire again.
 * Primarily useful in tests that need to assert the warning is re-emitted
 * after the component is reconfigured.
 */
export function resetWarnOnce(key: string): void {
  emittedWarnings.delete(key);
}

/**
 * Reset all emitted warning keys. Useful in test `afterEach` / `beforeEach`
 * hooks to keep test runs isolated.
 */
export function resetAllWarnOnce(): void {
  emittedWarnings.clear();
}
